import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RouteEnum } from '@/lib/components/TopBar/routeEnum';
import { Button } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import Select, { Option } from '@/lib/v2/components/Select/Select';
import Text from '@/lib/v2/components/Text/Text';

import ContactsMappingField, {
  ITableMappingField,
  matchValueSelector,
  noImport,
} from '@/src/ContactsModule/components/ContactsMappingField/ContactsMappingField';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue } from '@/src/infrastructure/Protocol/useEmblue';
import { useUserData } from '@/src/infrastructure/services/useUserData';
import {
  atomFileDataQuantity,
  atomListCustomField,
  atomMappingList,
} from '@/src/modules/ContactsModule/atoms/Step2';
import { DELIMITER_LIST } from '@/src/modules/ContactsModule/constants';
import { ImportSteps } from '@/src/modules/ContactsModule/screens/ImportMain/ImportStepEnum.enum';
import { StateOption } from '@/src/modules/ContactsModule/screens/ImportMain/StateOptionsEnum.enum';
import { CustomsFieldsProps } from '@/src/modules/ContactsModule/screens/ImportMain/V2ImportTypes';
import { ColumnProcessProps } from '@/src/presentation/types/interfaces/IImport.interface';

import {
  atomDelimiter,
  atomFiles,
  atomShowProgressNotification,
} from '@/modules/ContactsModule/atoms/Steps';
import { useResetImport } from '@/modules/ContactsModule/hooks/useResetImport';

interface IPropsStep2 {
  convertToColumnsProcess: () => ColumnProcessProps[];
}

const Step2 = (props: IPropsStep2) => {
  const { convertToColumnsProcess } = props;
  const [fileDataQuantity] = useAtom(atomFileDataQuantity);
  const { t } = useTranslation();
  const [files, setFiles] = useAtom(atomFiles);
  const [delimiter, setDelimiter] = useAtom(atomDelimiter);
  const [selectListCustomField] = useAtom(atomListCustomField);
  const [mappingList, setMappingList] = useAtom(atomMappingList);
  const [listCustomField] = useEmblue(ServiceMethods.getListCustomFields);
  const [userData] = useUserData();
  const [, setShowProgressNotification] = useAtom(atomShowProgressNotification);

  const navigate = useNavigate();
  const { resetImport } = useResetImport();

  const cancelProcess = () => {
    resetImport();
    navigate({ pathname: `/v2/${RouteEnum.ContactsImport}` });
  };

  const selectDelimiter = useCallback(
    (option: Option | Option[]) => {
      setDelimiter(option as Option);
    },
    [setDelimiter]
  );

  const onchangeDataType = useCallback(
    (option: Option, id: string | number) => {
      const mapList = mappingList?.mappingList;

      const index = mapList.findIndex((element: { mapping: ITableMappingField }) => {
        return element.mapping.mappingField.id === id;
      });
      const bitMapping = mapList.at(index)?.mapping;

      if (bitMapping?.formatColumn) {
        bitMapping.formatColumn.selected = option;
        mapList.splice(index, 1, { mapping: bitMapping });
        setMappingList({ mappingList: mapList });
      }
    },
    [mappingList, setMappingList]
  );

  const setMappingListOption = useCallback(
    (option: Option, id: string | number, idColumn: number) => {
      const { mappingList: mapList } = mappingList;
      const index =
        +id !== StateOption.noImport
          ? mapList.findIndex((element: { mapping: ITableMappingField }) => {
              return element.mapping.mappingField.id === id;
            })
          : idColumn;
      const replaceOptionIndex = mapList.findIndex((element: { mapping: ITableMappingField }) => {
        return element.mapping.mappingField.id === option.id;
      });
      const matchCustomFieldElement = listCustomField?.find((element: CustomsFieldsProps) => {
        return element.id === String(option.id);
      });

      const newMapping = mapList.at(index)?.mapping;
      if (newMapping) {
        newMapping.mappingField = option;
        newMapping.header = option.name.toString();

        if (matchCustomFieldElement) {
          const dataTypeSelectors = matchValueSelector(matchCustomFieldElement.dataType);
          newMapping.formatColumn = {
            dataType: matchCustomFieldElement.dataType,
            selectors: dataTypeSelectors,
            selected: dataTypeSelectors ? dataTypeSelectors.at(0) : undefined,
          };
        }
        mapList.splice(index, 1, { mapping: newMapping });
      }

      if (replaceOptionIndex !== StateOption.noImport) {
        const replaceOption = mapList.at(replaceOptionIndex)?.mapping;

        if (replaceOption) {
          const test: ITableMappingField = {
            idColumn: replaceOptionIndex,
            data: replaceOption.data,
            alreadyInUse: replaceOption.alreadyInUse,
            mappingField: noImport,
            header: noImport.name.toString(),
          };
          mapList.splice(replaceOptionIndex, 1, { mapping: test });
        }
      }
      setMappingList({ mappingList: mapList });
    },
    [listCustomField, mappingList, setMappingList]
  );

  const mappingListString = JSON.stringify(files?.mappingList?.mappingList);

  const emailIsSelectedInStep2 = useMemo(
    () => files?.mappingList?.mappingList.some((fields) => fields.mapping.header === 'email'),
    [mappingListString]
  );

  const mainEmailColumn = useCallback(() => {
    let position = 0;

    if (mappingList && Array.isArray(mappingList.mappingList))
      for (const map of mappingList.mappingList) {
        position++;
        if (map.mapping.header === 'email') {
          break;
        }
      }
    return position.toString();
  }, [mappingList]);

  const listColumnNames = useCallback(() => {
    const listColumns = mappingList?.mappingList
      ?.map((element: { mapping: ITableMappingField }) => {
        if (Number(element.mapping.mappingField.id) !== StateOption.noImport) {
          return element.mapping.header;
        }
      })
      ?.filter((element: string | undefined) => {
        return element !== undefined;
      }) as string[];

    return Array.isArray(listColumns) ? listColumns : [];
  }, [mappingList?.mappingList]);

  const nextToMappingField = useCallback(() => {
    const listColumns = listColumnNames();
    if (!listColumns) return;
    setFiles({
      ...files,
      columnsImports: {
        importColumn: files?.columnsImports?.importColumn || 0,
        noImportColumn: files?.columnsImports?.noImportColumn || 0,
        excludedColumn: files?.columnsImports?.excludedColumn || [],
        totalColumns: files?.columnsImports?.totalColumns || 0,
        columnsImportsNames: listColumns,
      },
      step: ImportSteps.dataCleaning,
      importsOptions: {
        ...files?.importsOptions,
        mainEmailColumn: mainEmailColumn(),
        columnsProcess: convertToColumnsProcess(),
        delimiter: delimiter.value as string,
      },
    });
    setShowProgressNotification(RESET);
    datadogRum.addAction('ContactsImport.step2Success', {
      email: userData.email,
      accountId: Number(userData.companyId),
    });
  }, [
    convertToColumnsProcess,
    delimiter.value,
    files,
    listColumnNames,
    mainEmailColumn,
    setFiles,
    setShowProgressNotification,
    userData.companyId,
    userData.email,
  ]);

  if (files.step !== ImportSteps.mappingField) return null;

  return (
    <Flex column itemAlignment="stretch">
      <Flex className="px-10 pb-1 pt-2" itemAlignment="center">
        <Flex>
          <Text variant="text">
            {fileDataQuantity?.quantityContacts} {t('IMPORT_MAIN.recognizedRegisters')}{' '}
            {fileDataQuantity?.quantityColumns} {t('IMPORT_MAIN.columns')}
          </Text>
        </Flex>
        <Flex withGap alignment="end" gapSize="medium">
          <Text color="primary" fontWeight="normal" variant="sub-headline">
            {t('IMPORT_MAIN.delimiterCapitalize')}
          </Text>
          <div className="w-72">
            <Select
              id="import-file-delimiter"
              options={DELIMITER_LIST}
              value={delimiter}
              onChange={selectDelimiter}
            />
          </div>
        </Flex>
      </Flex>
      {selectListCustomField && files?.mappingList && (
        <div className="mt-5 px-4">
          <ContactsMappingField
            headers={files?.headers}
            listCustomField={selectListCustomField}
            mappingList={files?.mappingList}
            onchangeDataType={onchangeDataType}
            setMappingList={setMappingListOption}
          />
        </div>
      )}
      <Flex column alignment="end" itemAlignment="end">
        <Flex alignment="end" className="border-emblue-border flex border-y py-6 pr-10">
          {!emailIsSelectedInStep2 && (
            <Text color="alert" variant="text">
              {t('IMPORT_MAIN.mappingEmailFieldIsRequired')}
            </Text>
          )}

          {files?.columnsImports && emailIsSelectedInStep2 && (
            <Text variant="text">
              {t('IMPORT_MAIN.willBeImported')}{' '}
              <span className="font-medium text-emblue-primary">
                {files?.columnsImports.importColumn}
              </span>{' '}
              {t('IMPORT_MAIN.of')}{' '}
            </Text>
          )}

          {files?.columnsImports &&
            emailIsSelectedInStep2 &&
            files?.columnsImports.totalColumns !== 0 && (
              <Text className="px-1" variant="text">
                <span className="font-medium text-emblue-primary">
                  {files?.columnsImports.totalColumns}{' '}
                </span>
                {t('IMPORT_MAIN.columns')}
              </Text>
            )}
        </Flex>
        <Flex withGap alignment="end" className="py-2 pr-10" gapSize="small">
          <Button outline secondary id="import-cancel" onClick={cancelProcess}>
            {t('IMPORT_MAIN.cancel')}
          </Button>
          <Button
            disabled={!emailIsSelectedInStep2}
            id="import-continue"
            onClick={nextToMappingField}
          >
            {t('IMPORT_MAIN.next')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default memo(Step2);
